/**
 * edgeMapping — marker / label / highlight selection for EDG edges (phase 6b).
 *
 * Direct ports of the marker-and-label logic from `components/EDG/dependency.js`
 * and `components/EDG/inheritance.js`. Keeping the per-notation, per-highlight
 * marker-id choices here (rather than inline in the edge components) lets
 * `EdgFlow.deriveEdges` build the full edge `data` upstream, where the uimodel
 * node rects are already in scope for the geometry.
 *
 * The marker-id strings line up 1:1 with the ids declared in `markers.tsx`
 * (`OPTIONAL`, `MANDATORY`, `MANY`, `INHERIT`, `INHERIT_ABSTRACT` + the four
 * highlight-colour variants), so `markerStart`/`markerEnd` can reference them by
 * `url(#id)`.
 */
import {
  OPTIONAL,
  MANDATORY,
  MANY,
  INHERIT,
  INHERIT_ABSTRACT,
  MARKER_VARIANTS,
} from './markerIds';

export type Notation = 'MERODE' | 'UML';
export type DependencyType =
  | 'OPTIONAL_N'
  | 'OPTIONAL_1'
  | 'MANDATORY_N'
  | 'MANDATORY_1';

/**
 * The four highlight flags for an edge, derived from the four observable id-lists
 * on the uimodel/EDG UI store (`highlighted`, `highlightedPath`, `primaryPath`,
 * `secondaryPath`). One edge can be in several lists; the marker selection below
 * uses the SAME precedence the old EDG code used.
 */
export type EdgeHighlight = {
  highlighted: boolean;
  highlightedPath: boolean;
  primaryPath: boolean;
  secondaryPath: boolean;
};

/** Labels for the dependent side in UML notation (port of `DEPENDENT_LABELS`). */
export const DEPENDENT_LABELS: Record<DependencyType, string> = {
  OPTIONAL_N: '0..*',
  OPTIONAL_1: '0..1',
  MANDATORY_N: '1..*',
  MANDATORY_1: '1..1',
};

/**
 * Source markers for a dependency (port of `MERODE_SOURCE_MARKERS`,
 * dependency.js:18). Precedence: highlighted > highlightedPath > primaryPath >
 * secondaryPath > default — EXACTLY the order the old code used (note this is a
 * different order than the node highlight precedence).
 */
function merodeSourceMarkers(h: EdgeHighlight): { OPTIONAL: string; MANDATORY: string } {
  let optionalId = OPTIONAL;
  let mandatoryId = MANDATORY;
  if (h.highlighted) {
    optionalId = MARKER_VARIANTS.HL.OPTIONAL;
    mandatoryId = MARKER_VARIANTS.HL.MANDATORY;
  } else if (h.highlightedPath) {
    optionalId = MARKER_VARIANTS.HLP.OPTIONAL;
    mandatoryId = MARKER_VARIANTS.HLP.MANDATORY;
  } else if (h.primaryPath) {
    optionalId = MARKER_VARIANTS.PRIMARY.OPTIONAL;
    mandatoryId = MARKER_VARIANTS.PRIMARY.MANDATORY;
  } else if (h.secondaryPath) {
    optionalId = MARKER_VARIANTS.SECONDARY.OPTIONAL;
    mandatoryId = MARKER_VARIANTS.SECONDARY.MANDATORY;
  }
  return { OPTIONAL: optionalId, MANDATORY: mandatoryId };
}

/** Target markers for a dependency (port of `MERODE_TARGET_MARKERS`). */
function merodeTargetMarkers(h: EdgeHighlight): { N: string } {
  let id = MANY;
  if (h.highlighted) {
    id = MARKER_VARIANTS.HL.MANY;
  } else if (h.highlightedPath) {
    id = MARKER_VARIANTS.HLP.MANY;
  } else if (h.primaryPath) {
    id = MARKER_VARIANTS.PRIMARY.MANY;
  } else if (h.secondaryPath) {
    id = MARKER_VARIANTS.SECONDARY.MANY;
  }
  return { N: id };
}

export type DependencyMarkers = {
  markerStartId: string | null;
  markerEndId: string | null;
};

/**
 * Pick the start/end marker ids for a dependency, mirroring
 * `Dependency.synchronizeMarkers` (dependency.js:87). Only MERODE notation has
 * markers; UML uses cardinality labels instead. The source marker is keyed by the
 * FIRST half of the type (`OPTIONAL` / `MANDATORY`); the target marker by the
 * SECOND half (`N` / `1` — only `N` carries a marker, `1` has none, matching the
 * old `MERODE_TARGET_MARKERS` which only defines `N`).
 */
export function dependencyMarkers(
  type: DependencyType,
  notation: Notation,
  h: EdgeHighlight
): DependencyMarkers {
  if (notation !== 'MERODE') {
    return { markerStartId: null, markerEndId: null };
  }
  const [first, second] = type.split('_') as ['OPTIONAL' | 'MANDATORY', 'N' | '1'];
  const source = merodeSourceMarkers(h)[first] ?? null;
  // MERODE_TARGET_MARKERS only defines `N`; a `1` dependent has no end marker
  // (undefined in the old code => marker-end removed).
  const target = second === 'N' ? merodeTargetMarkers(h).N : null;
  return { markerStartId: source, markerEndId: target };
}

/**
 * Pick the inheritance marker id (verbatim port of `inheritance.js:marker`). The
 * inheritance triangle is the SOURCE (supertype-side) marker; there is no end
 * marker and no label. An inheritance ALWAYS has a marker, in BOTH notations:
 * the old `marker()` only used the FILLED `inherit-abstract` glyph when the
 * supertype is abstract AND notation is MERODE; in every other case (incl. all
 * of UML, and concrete supertypes in MERODE) it returns the HOLLOW `inherit`
 * triangle. (This is unlike dependencies, where UML drops markers entirely.)
 * Precedence: highlighted > highlightedPath > primaryPath > secondaryPath.
 */
export function inheritanceMarker(
  abstract: boolean,
  notation: Notation,
  h: EdgeHighlight
): string | null {
  if (abstract && notation === 'MERODE') {
    if (h.highlighted) return MARKER_VARIANTS.HL.INHERIT_ABSTRACT;
    if (h.highlightedPath) return MARKER_VARIANTS.HLP.INHERIT_ABSTRACT;
    if (h.primaryPath) return MARKER_VARIANTS.PRIMARY.INHERIT_ABSTRACT;
    if (h.secondaryPath) return MARKER_VARIANTS.SECONDARY.INHERIT_ABSTRACT;
    return INHERIT_ABSTRACT;
  }
  if (h.highlighted) return MARKER_VARIANTS.HL.INHERIT;
  if (h.highlightedPath) return MARKER_VARIANTS.HLP.INHERIT;
  if (h.primaryPath) return MARKER_VARIANTS.PRIMARY.INHERIT;
  if (h.secondaryPath) return MARKER_VARIANTS.SECONDARY.INHERIT;
  return INHERIT;
}

export type DependencyLabelSpec = {
  /** Label near the source (UML: '1'), or undefined. */
  sourceLabel?: string;
  /** Label near the centre (peer display name), or undefined. */
  centerLabel?: string;
  /** Label near the target (UML: cardinality), or undefined. */
  targetLabel?: string;
};

/**
 * How many existence dependencies share this master → dependent pair.
 * The centre name is shown only when this is greater than 1.
 */
export function peerCountForDependency(dep: {
  master: { dependenciesAsMaster: ReadonlyArray<{ dependent: unknown }> };
  dependent: unknown;
}): number {
  return dep.master.dependenciesAsMaster.filter((d) => d.dependent === dep.dependent).length;
}

/**
 * Centre label for an ED: the display name only when two (or more) EDs share
 * the same classes. Derived from the metamodel, not from leftover UI links,
 * so deleting one of a pair hides the name on the remaining edge.
 */
export function peerLabelForDependency(dep: {
  displayName: string;
  master: { dependenciesAsMaster: ReadonlyArray<{ dependent: unknown }> };
  dependent: unknown;
}): string | undefined {
  return peerCountForDependency(dep) > 1 ? dep.displayName : undefined;
}

/**
 * Resolve the label spec for a dependency, mirroring
 * `Dependency.synchronizeLabels` (dependency.js:115) + `setDependencyLabels`:
 *   - UML notation: source label '1', target label = cardinality (DEPENDENT_LABELS),
 *     centre label = the peer display name (only present when the link has peers).
 *   - MERODE notation: no source/target labels, only the centre peer label.
 *
 * `peerLabel` is `dep.displayName` when two or more EDs share the same
 * master/dependent pair (`peerLabelForDependency`), else undefined.
 */
export function dependencyLabels(
  type: DependencyType,
  notation: Notation,
  peerLabel: string | undefined
): DependencyLabelSpec {
  if (notation === 'UML') {
    return {
      sourceLabel: '1',
      targetLabel: DEPENDENT_LABELS[type],
      centerLabel: peerLabel,
    };
  }
  return { centerLabel: peerLabel };
}
